import { SavedValuation, ValuationData } from '../types/valuation';

export interface PortfolioSummary {
  propertyCount: number;
  totalValue: number;
  avgValue: number;
  avgFuelMargin: number;
  totalAnnualGallons: number;
  avgAnnualGallons: number;
  avgStoreRevenue: number;
  highestValued: { location: string; value: number } | null;
  lowestValued: { location: string; value: number } | null;
}

function calculateEstimatedValue(data: ValuationData): number {
  // Use API response if available
  if (data.apiResponse?.result?.total_station_value) {
    return data.apiResponse.result.total_station_value;
  }

  const fuelRevenue = (data.annualGallons || 0) * (data.fuelMargin || 0);
  const storeRevenue = (data.storeRevenue || 0) * 12;
  const expenses = (data.operatingExpenses || 0) * 12;
  const netIncome = fuelRevenue + storeRevenue - expenses;
  const multiplier = 3.5;
  return netIncome * multiplier || 0;
}

export function calculatePortfolioSummary(
  valuations: SavedValuation[]
): PortfolioSummary {
  if (valuations.length === 0) {
    return {
      propertyCount: 0,
      totalValue: 0,
      avgValue: 0,
      avgFuelMargin: 0,
      totalAnnualGallons: 0,
      avgAnnualGallons: 0,
      avgStoreRevenue: 0,
      highestValued: null,
      lowestValued: null,
    };
  }

  const values = valuations.map((v) => ({
    location: v.data.location || 'Unknown',
    value: calculateEstimatedValue(v.data),
    fuelMargin: v.data.fuelMargin || 0,
    annualGallons: v.data.annualGallons || 0,
    storeRevenue: v.data.storeRevenue || 0,
  }));

  const totalValue = values.reduce((sum, v) => sum + v.value, 0);
  const totalGallons = values.reduce((sum, v) => sum + v.annualGallons, 0);
  const totalMargin = values.reduce((sum, v) => sum + v.fuelMargin, 0);
  const totalStoreRevenue = values.reduce((sum, v) => sum + v.storeRevenue, 0);

  const sorted = [...values].sort((a, b) => b.value - a.value);

  return {
    propertyCount: valuations.length,
    totalValue,
    avgValue: totalValue / valuations.length,
    avgFuelMargin: totalMargin / valuations.length,
    totalAnnualGallons: totalGallons,
    avgAnnualGallons: totalGallons / valuations.length,
    avgStoreRevenue: totalStoreRevenue / valuations.length,
    highestValued: sorted[0]
      ? { location: sorted[0].location, value: sorted[0].value }
      : null,
    lowestValued: sorted[sorted.length - 1]
      ? {
          location: sorted[sorted.length - 1].location,
          value: sorted[sorted.length - 1].value,
        }
      : null,
  };
}
